import { Button, Flex, Typography, Carousel } from "antd";
import type { CarouselRef } from "antd/es/carousel";

import dayjs, { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { ISingleAvailability } from "types";
import { StyledNotification, Timeslot, TimeslotsContainer } from "./styles";
import { useApp } from "contexts/AppProvider";
import { useIntl } from "react-intl";
import { useScreenDetector } from "lib/useScreenDetector";

const timeslotsToShow = 3;

export const getHours = (start: Dayjs, end: Dayjs, duration = 60, advanceHours: number) => {
  const hours = [];
  let current = start;

  const minAllowedDateTime = dayjs().add(advanceHours, "hours");
  const endTime = end.subtract(duration, "minutes");
  const endTimeAdjusted = end.subtract(duration, "minutes");
  while (current.isBefore(endTime, "minute") || current.isSame(endTimeAdjusted, "minute")) {
    if (current.isAfter(minAllowedDateTime) || current.isSame(minAllowedDateTime)) {
      hours.push(current.format("HH:mm"));
    }

    current = current.add(duration, "minutes");
  }

  return hours;
};

type ParsedAvailabilityType = {
  [key: string]: ISingleAvailability[];
};

export const AvailabilitySlots = ({
  availability,
  setTimeSchedules,
  duration,
  onLoadMore,
  isLoadingMore,
  loadedDays = 30,
  persistedAnchorDate = null,
  onAnchorDateChange,
}) => {
  const { setActiveTimeslot, activeTimeslot, mainColor, reservationPayload } = useApp();
  const [parsedAvailability, setParsedAvailability] = useState<ParsedAvailabilityType | null>(null);
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [showMoreClicked, setShowMoreClicked] = useState<boolean>(false);
  const [firstAvailableClicked, setFirstAvailableClicked] = useState<boolean>(false);
  const pendingAnchorRef = useRef<string | null>(null);
  const isFirstAvailableNavigationRef = useRef<boolean>(false);

  const { formatMessage } = useIntl();
  const { isMobile } = useScreenDetector();
  const [firstAvailableIndex, setFirstAvailableIndex] = useState<number>(0);
  const slidesToShow = isMobile ? 3 : 5;
  const activeSlideRef = useRef<number>(0);
  const parsedAvailabilityRef = useRef<ParsedAvailabilityType | null>(null);
  const persistedAnchorDateRef = useRef<string | null>(persistedAnchorDate);

  useEffect(() => {
    activeSlideRef.current = activeSlide;
  }, [activeSlide]);

  useEffect(() => {
    parsedAvailabilityRef.current = parsedAvailability;
  }, [parsedAvailability]);

  useEffect(() => {
    persistedAnchorDateRef.current = persistedAnchorDate;
  }, [persistedAnchorDate]);

  useEffect(() => {
    if (availability) {
      const existingKeys = parsedAvailabilityRef.current ? Object.keys(parsedAvailabilityRef.current) : [];
      const currentVisibleDate = existingKeys[activeSlideRef.current] || null;
      pendingAnchorRef.current = persistedAnchorDateRef.current || currentVisibleDate;

      let dateFrom = dayjs().format("YYYY-MM-DD");
      const dateTo = dayjs().add(loadedDays, "days").format("YYYY-MM-DD");

      const dates = {};

      while (dateFrom <= dateTo) {
        dates[dateFrom] = {};
        if (dateFrom === dateTo) {
          break;
        }
        dateFrom = dayjs(dateFrom).add(1, "day").format("YYYY-MM-DD");
      }

      availability.forEach((el) => {
        const times = el.timeslots;

        if (times.length === 0) {
          return;
        }

        if (!dates[el.date]) {
          dates[el.date] = {};
        }

        times.forEach((time) => {
          if (!dates[el.date][time]) {
            dates[el.date][time] = [];
          }
          dates[el.date][time].push({ ...el, startTime: time });
        });
      });

      setParsedAvailability(dates);
      setFirstAvailableClicked(false);
    }
  }, [availability, loadedDays]);

  useEffect(() => {
    if (parsedAvailability) {
      const firstAvailable = Object.keys(parsedAvailability).findIndex(
        (key) => Object.keys(parsedAvailability[key]).length > 0,
      );
      if (firstAvailable !== -1) {
        setFirstAvailableIndex(firstAvailable);
      }
    }
  }, [parsedAvailability]);

  useEffect(() => {
    if (parsedAvailability) {
      let hasMore = false;
      for (let i = activeSlide; i < activeSlide + slidesToShow; i++) {
        const dateKey = Object.keys(parsedAvailability)[i];
        if (dateKey && Object.keys(parsedAvailability[dateKey]).length > timeslotsToShow) {
          hasMore = true;
          break;
        }
      }
      setShowMore(hasMore);
    }
  }, [activeSlide, parsedAvailability, slidesToShow]);

  const carouselRef = useRef<CarouselRef>(null);

  useEffect(() => {
    if (!activeTimeslot || !carouselRef.current) {
      return;
    }

    // Updating the active timeslot changes a child of react-slick, which can
    // make it jump back to slide 0. Keep the user's current date range visible.
    carouselRef.current.goTo(activeSlideRef.current, false);
  }, [activeTimeslot]);

  useEffect(() => {
    if (!parsedAvailability || !carouselRef.current) {
      return;
    }

    const dateKeys = Object.keys(parsedAvailability);
    const maxSlide = Math.max(0, dateKeys.length - slidesToShow);
    const anchorIndex = pendingAnchorRef.current ? dateKeys.indexOf(pendingAnchorRef.current) : -1;

    // react-slick can report slide 0 while it reinitializes after more children
    // are appended. Restore by date rather than by that transient slide index.
    const nextSlide = Math.min(anchorIndex >= 0 ? anchorIndex : activeSlideRef.current, maxSlide);
    if (nextSlide !== activeSlide) {
      setActiveSlide(nextSlide);
    }

    carouselRef.current.goTo(nextSlide, false);
    pendingAnchorRef.current = null;
    onAnchorDateChange?.(dateKeys[nextSlide] || null);
  }, [parsedAvailability, slidesToShow]);

  return (
    <TimeslotsContainer>
      {parsedAvailability && (
        <>
          <div
            style={{
              overflow: "visible",
              minHeight: "170px",
              transition: "height 0.5s",
            }}
          >
            <Carousel
              infinite={false}
              ref={carouselRef}
              arrows
              dots={false}
              slidesToShow={slidesToShow}
              slidesToScroll={slidesToShow}
              afterChange={(currentSlide) => {
                setActiveSlide(currentSlide);
                setShowMoreClicked(false);
                if (!isFirstAvailableNavigationRef.current) {
                  onAnchorDateChange?.(Object.keys(parsedAvailability)[currentSlide] || null);
                  // Load more days if near the end
                  if (
                    parsedAvailability &&
                    onLoadMore &&
                    !isLoadingMore &&
                    currentSlide + slidesToShow >= Object.keys(parsedAvailability).length - slidesToShow
                  ) {
                    onLoadMore();
                  }
                } else {
                  isFirstAvailableNavigationRef.current = false;
                }
              }}
            >
              {Object.keys(parsedAvailability).map((date) => (
                <div key={date}>
                  <Flex vertical align="center">
                    <div>
                      <Typography.Text strong>{dayjs(date).format("DD MMM")}</Typography.Text>
                    </div>
                    <div>({dayjs(date).format("ddd")})</div>
                    <Flex vertical align="center">
                      {Object.keys(parsedAvailability[date]).length === 0 ? (
                        <Flex vertical>
                          <div style={{ padding: "10px" }}>-</div>
                          <div style={{ padding: "10px" }}>-</div>
                          <div style={{ padding: "10px" }}>-</div>
                        </Flex>
                      ) : (
                        <>
                          {Object.keys(parsedAvailability[date])
                            .sort((a, b) =>
                              dayjs(`${date} ${a}`, "YYYY-MM-DD HH:mm").diff(dayjs(`${date} ${b}`, "YYYY-MM-DD HH:mm")),
                            )
                            .map((el, index) => {
                              if (showMore && index >= timeslotsToShow) {
                                return null;
                              }
                              return parsedAvailability[date][el].map((slot) => {
                                const isOnline = slot.isOnline || reservationPayload?.isOnline;
                                return (
                                  <Timeslot
                                    as="button"
                                    type="button"
                                    style={{ width: "100%", border: 0, textAlign: "left" }}
                                    mainColor={mainColor}
                                    active={
                                      activeTimeslot?.scheduleId === slot.scheduleId &&
                                      activeTimeslot?.startTime === slot.startTime
                                    }
                                    key={`${slot.scheduleId}_${slot.date}_${slot.startTime}`}
                                    onClick={() => {
                                      if (showMoreClicked) {
                                        setShowMoreClicked(false);
                                        setShowMore(true);
                                      }
                                      const selectedSlot = { ...slot, isOnline: !!isOnline };
                                      setTimeSchedules([selectedSlot]);
                                      setActiveTimeslot(selectedSlot);
                                    }}
                                  >
                                    {slot.startTime}
                                  </Timeslot>
                                );
                              });
                            })}
                        </>
                      )}
                    </Flex>
                  </Flex>
                </div>
              ))}
            </Carousel>
          </div>

          {isLoadingMore && (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <Typography.Text type="secondary">
                {formatMessage({ defaultMessage: "Ładowanie kolejnych terminów...", id: "Eq6blZ" })}
              </Typography.Text>
            </div>
          )}

          {showMore && (
            <div
              style={{
                textAlign: "center",
                marginTop: 10,
                padding: "10px 0",
              }}
            >
              <Button type="text" onClick={() => [setShowMore(false), setShowMoreClicked(true)]}>
                {formatMessage({
                  defaultMessage: "Pokaż więcej godzin",
                  id: "vjo0Qa",
                })}
              </Button>
            </div>
          )}
          {firstAvailableIndex >= slidesToShow &&
            activeSlide + slidesToShow <= firstAvailableIndex &&
            !firstAvailableClicked && (
              <StyledNotification
                vertical
                isButton
                align="center"
                onClick={() => {
                  const firstAvailableDate = Object.keys(parsedAvailability)[firstAvailableIndex] || null;

                  activeSlideRef.current = firstAvailableIndex;
                  persistedAnchorDateRef.current = firstAvailableDate;
                  isFirstAvailableNavigationRef.current = true;
                  setFirstAvailableClicked(true);
                  setActiveSlide(firstAvailableIndex);
                  onAnchorDateChange?.(firstAvailableDate);
                  carouselRef.current?.goTo(firstAvailableIndex, false);
                }}
              >
                <Typography.Text>
                  {formatMessage({
                    defaultMessage: "Zobacz pierwszy dostępny termin:",
                    id: "MLtSZi",
                  })}
                </Typography.Text>
                <Typography.Text strong>
                  {dayjs(Object.keys(parsedAvailability)[firstAvailableIndex]).format("DD MMMM YYYY")}
                </Typography.Text>
              </StyledNotification>
            )}
        </>
      )}
    </TimeslotsContainer>
  );
};
